import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Hash, Send, Loader2, MessageSquare, Users, Shield, Briefcase,
  TrendingUp, AlertTriangle, Crown, Link2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_ICONS = {
  general: MessageSquare,
  originations: Briefcase,
  underwriting: Shield,
  processing: Loader2,
  "lender-relations": TrendingUp,
  "investor-relations": Users,
  management: Crown,
  "ops-alerts": AlertTriangle,
};

const ROLE_BADGE = {
  admin: "bg-purple-100 text-purple-700",
  originations: "bg-teal-100 text-teal-700",
  underwriter: "bg-indigo-100 text-indigo-700",
  processor: "bg-cyan-100 text-cyan-700",
  lender_relations: "bg-lime-100 text-lime-700",
  investor_relations: "bg-orange-100 text-orange-700",
};

export default function DepartmentChannels() {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const pollRef = useRef(null);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const currentUser = (() => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return { userId: payload.user_id || payload.sub, role: payload.role, name: payload.full_name };
    } catch { return {}; }
  })();

  const fetchChannels = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/channels`, { headers });
      setChannels(res.data.channels || []);
      if (!activeChannel && res.data.channels?.length) {
        setActiveChannel(res.data.channels[0].channel_id);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (channelId) => {
    if (!channelId) return;
    setMsgLoading(true);
    try {
      const res = await axios.get(`${API}/channels/${channelId}/messages`, { headers });
      setMessages(res.data.messages || []);
    } catch (err) { console.error(err); }
    finally { setMsgLoading(false); }
  }, []);

  useEffect(() => { fetchChannels(); }, [fetchChannels]);

  useEffect(() => {
    if (activeChannel) {
      fetchMessages(activeChannel);
      // Poll every 5s
      pollRef.current = setInterval(() => fetchMessages(activeChannel), 5000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeChannel, fetchMessages]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || !activeChannel) return;
    setSending(true);
    try {
      await axios.post(`${API}/channels/${activeChannel}/messages`, { content: text }, { headers });
      setInput("");
      fetchMessages(activeChannel);
      fetchChannels(); // Update unread counts
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const activeChData = channels.find(c => c.channel_id === activeChannel);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;

  return (
    <div className="flex rounded-2xl border border-cream-warm bg-white overflow-hidden" style={{ height: "520px" }} data-testid="department-channels">
      {/* Channel Sidebar */}
      <div className="w-56 border-r border-cream-warm bg-cream/50 flex flex-col shrink-0">
        <div className="px-4 py-3 border-b border-cream-warm">
          <h3 className="text-xs font-bold text-[#444] uppercase tracking-wider">Channels</h3>
        </div>
        <div className="flex-1 overflow-y-auto py-1">
          {channels.filter(c => c.accessible).map(ch => {
            const Icon = CHANNEL_ICONS[ch.name] || Hash;
            const isActive = ch.channel_id === activeChannel;
            return (
              <button
                key={ch.channel_id}
                onClick={() => setActiveChannel(ch.channel_id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                  isActive ? "bg-emerald-50 border-r-2 border-emerald-600" : "hover:bg-cream"
                }`}
                data-testid={`channel-${ch.name}`}
              >
                <Icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-emerald-600" : "text-[#888]"}`} />
                <span className={`text-sm truncate ${isActive ? "text-emerald-700 font-semibold" : "text-[#555]"}`}>
                  {ch.name}
                </span>
                {ch.unread_count > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold min-w-[18px] text-center" data-testid={`unread-${ch.name}`}>
                    {ch.unread_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Message Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Channel Header */}
        {activeChData && (
          <div className="px-4 py-3 border-b border-cream-warm bg-cream/30 flex items-center gap-2">
            <Hash className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-bold text-[#333]">{activeChData.name}</span>
            <span className="text-xs text-[#999] ml-2">{activeChData.description}</span>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0" data-testid="channel-messages">
          {msgLoading && messages.length === 0 ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-sm text-[#999]">No messages yet. Start the conversation.</div>
          ) : (
            messages.map(msg => {
              const isMine = msg.user_id === currentUser.userId;
              const badgeClass = ROLE_BADGE[msg.user_role] || "bg-slate-100 text-slate-600";
              return (
                <motion.div key={msg.msg_id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="flex gap-2.5 items-start">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0 ${
                    isMine ? "bg-emerald-600" : "bg-slate-400"
                  }`}>
                    {(msg.user_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${isMine ? "text-emerald-700" : "text-[#333]"}`}>{msg.user_name}</span>
                      <span className={`px-1.5 py-0 rounded text-[9px] font-bold uppercase ${badgeClass}`}>{msg.user_role}</span>
                      <span className="text-[10px] text-[#bbb]">
                        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                      </span>
                    </div>
                    <p className="text-sm text-[#333] mt-0.5 break-words">{msg.content}</p>
                    {msg.deal_id && (
                      <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-[10px] font-medium">
                        <Link2 className="h-2.5 w-2.5" /> Deal: {msg.deal_id.slice(0, 8)}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })
          )}
          <div ref={endRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-cream-warm flex items-center gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder={activeChData ? `Message #${activeChData.name}...` : "Select a channel..."}
            disabled={!activeChannel}
            data-testid="channel-message-input"
            className="flex-1 text-sm px-3 py-2.5 rounded-xl border border-cream-warm bg-cream text-[#0A0A0A] placeholder:text-[#999] focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 disabled:opacity-50"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            data-testid="channel-send-btn"
            className="p-2.5 rounded-xl bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
